<script setup lang="ts">
const { t, tm, rt } = useI18n();
const site = useSiteData();
const { localized } = useLocaleValue();
const lastUpdatedDate = "2026-01-01";
const tmList = (key: string) => tm(key) as unknown[];
const tmText = (item: unknown) => rt(item as Parameters<typeof rt>[0]);
const tmHtml = (item: unknown) => rt(item as Parameters<typeof rt>[0]);

definePageMeta({
  page_name: "cookie",
  robots: "noindex, follow",
});
</script>

<template>
  <section class="page-header">
    <div class="container">
      <h1>{{ t("cookie.header.title") }}</h1>
    </div>
  </section>

  <section class="about-content">
    <div class="container">
      <div class="about-section">
        <p>
          <strong>{{ t("cookie.last_updated_label") }}</strong>
          {{ lastUpdatedDate }}
        </p>
        <p>{{ t("cookie.intro.p1") }}</p>
        <p>
          {{ t("cookie.intro.p2_prefix") }}
          <NuxtLink to="/privacy-policy">
            {{ t("cookie.intro.p2_link_label") }}
          </NuxtLink>.
        </p>
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.what.title") }}</h2>
        <p>{{ t("cookie.sections.what.p1") }}</p>
        <p>{{ t("cookie.sections.what.p2") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.how.title") }}</h2>
        <p>{{ t("cookie.sections.how.p1") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('cookie.sections.how.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
        <p v-html="t('cookie.sections.how.p2')" />
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.types.title") }}</h2>

        <h3>{{ t("cookie.sections.types.necessary.title") }}</h3>
        <p>{{ t("cookie.sections.types.necessary.p1") }}</p>
        <p>{{ t("cookie.sections.types.necessary.p2") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('cookie.sections.types.necessary.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
        <p>
          <strong>{{ t("cookie.sections.types.necessary.legal_basis_label") }}</strong>
          {{ t("cookie.sections.types.necessary.legal_basis_text") }}
        </p>

        <h3>{{ t("cookie.sections.types.analytics.title") }}</h3>
        <p>{{ t("cookie.sections.types.analytics.p1") }}</p>
        <p v-html="t('cookie.sections.types.analytics.p2')" />
        <p v-html="t('cookie.sections.types.analytics.p3')" />
        <p>{{ t("cookie.sections.types.analytics.p4") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('cookie.sections.types.analytics.items')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>
        <p>
          <strong>{{ t("cookie.sections.types.analytics.legal_basis_label") }}</strong>
          {{ t("cookie.sections.types.analytics.legal_basis_text") }}
        </p>
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.table.title") }}</h2>
        <p>{{ t("cookie.sections.table.p1") }}</p>

        <h3>{{ t("cookie.sections.table.necessary.title") }}</h3>
        <table style="width: 100%; border-collapse: collapse; margin: 1rem 0">
          <thead>
            <tr style="background-color: var(--bg-light)">
              <th style="padding: 0.75rem; text-align: left; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.headers.cookie_name") }}
              </th>
              <th style="padding: 0.75rem; text-align: left; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.headers.provider") }}
              </th>
              <th style="padding: 0.75rem; text-align: left; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.headers.purpose") }}
              </th>
              <th style="padding: 0.75rem; text-align: left; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.headers.duration") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                <code>cookie_consent</code>
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.necessary.row.provider") }}
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.necessary.row.purpose") }}
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.necessary.row.duration") }}
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <strong>{{ t("cookie.sections.table.notes_label") }}</strong>
        </p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('cookie.sections.table.necessary.notes')"
            :key="index"
          >
            {{ tmText(item) }}
          </li>
        </ul>

        <h3>{{ t("cookie.sections.table.analytics.title") }}</h3>
        <table style="width: 100%; border-collapse: collapse; margin: 1rem 0">
          <thead>
            <tr style="background-color: var(--bg-light)">
              <th style="padding: 0.75rem; text-align: left; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.headers.cookie_name") }}
              </th>
              <th style="padding: 0.75rem; text-align: left; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.headers.provider") }}
              </th>
              <th style="padding: 0.75rem; text-align: left; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.headers.purpose") }}
              </th>
              <th style="padding: 0.75rem; text-align: left; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.headers.duration") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                <code>_ga</code>
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.ga.provider") }}
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.ga.purpose") }}
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.ga.duration") }}
              </td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                <code>_ga_*</code>
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.ga_all.provider") }}
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.ga_all.purpose") }}
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.ga_all.duration") }}
              </td>
            </tr>
            <tr>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                <code>_gid</code>
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.gid.provider") }}
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.gid.purpose") }}
              </td>
              <td style="padding: 0.75rem; border: 1px solid var(--border-color)">
                {{ t("cookie.sections.table.analytics.rows.gid.duration") }}
              </td>
            </tr>
          </tbody>
        </table>
        <p>
          <strong>{{ t("cookie.sections.table.notes_label") }}</strong>
        </p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('cookie.sections.table.analytics.notes')"
            :key="index"
            v-html="tmHtml(item)"
          />
        </ul>
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.consent.title") }}</h2>
        <p>{{ t("cookie.sections.consent.p1") }}</p>
        <ul class="privacy-list">
          <li
            v-for="(item, index) in tmList('cookie.sections.consent.items')"
            :key="index"
            v-html="tmHtml(item)"
          />
        </ul>
        <p>{{ t("cookie.sections.consent.p2") }}</p>
        <p v-html="t('cookie.sections.consent.p3')" />
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.retention.title") }}</h2>
        <p>{{ t("cookie.sections.retention.p1") }}</p>
        <p>{{ t("cookie.sections.retention.p2") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.third_party.title") }}</h2>
        <p>{{ t("cookie.sections.third_party.p1") }}</p>
        <p>{{ t("cookie.sections.third_party.p2") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.changes.title") }}</h2>
        <p>{{ t("cookie.sections.changes.p1") }}</p>
      </div>

      <div class="about-section">
        <h2>{{ t("cookie.sections.contact.title") }}</h2>
        <p>{{ t("cookie.sections.contact.p1") }}</p>
        <p>
          <strong>{{ localized(site.company_full_legal_name) }}</strong><br />
          {{
            localized(site.legal_address.building_number)
          }}
          {{
            localized(site.legal_address.street_name)
          }}{{ t("common.comma") }}
          {{
            localized(site.legal_address.additional_number)
          }}
          {{
            localized(site.legal_address.district)
          }}{{ t("common.comma") }}
          {{
            localized(site.legal_address.postal_code)
          }}
          {{
            localized(site.legal_address.city)
          }}{{ t("common.comma") }}
          {{ localized(site.legal_address.country) }}<br />
          {{ t("cookie.sections.contact.email_label") }}
          <a :href="`mailto:${localized(site.official_email)}`">
            {{ localized(site.official_email) }}
          </a>
        </p>
        <p>
          {{ t("cookie.sections.contact.footer_prefix") }}
          <NuxtLink to="/contact">
            {{ t("cookie.sections.contact.contact_link_label") }}
          </NuxtLink>.
        </p>
      </div>
    </div>
  </section>
</template>

